
// Utility functions for generating CSV templates for data import

export interface TemplateData {
  headers: string[];
  exampleRows: string[][];
}

export function generateKolDbTemplate(): TemplateData {
  const headers = [
    'creator_name',
    'social_media_type',
    'social_media_handler',
    'social_media_name',
    'payment_structure',
    'currency_type',
    'region',
    'tier',
    'content_type',
    'start_date',
    'end_date'
  ];

  const exampleRows = [
    [
      'John Smith',
      'Twitter',
      '@johnsmith_official',
      'John Smith Official',
      'One time payment',
      'USD',
      'EN',
      'Tier 1',
      'Twitter - Tweet;Twitter - Thread/long post',
      '2024-01-01',
      '2024-12-31'
    ],
    [
      'Sarah Chen',
      'Youtube',
      '@sarahchentech',
      'Sarah Chen Tech',
      'Milestone based',
      'EUR',
      'VN',
      'Tier 2',
      'Telegram - post',
      '2024-02-15',
      '2024-08-15'
    ],
    [
      'Mike Rodriguez',
      'Telegram',
      '@mikerod_gaming',
      'Mike Gaming',
      'Quarterly',
      'USD',
      'RU',
      'Tier 1',
      'Twitter - Tweet',
      '2024-03-01',
      '2024-09-30'
    ]
  ];

  return { headers, exampleRows };
}

export function generateKolTrackerTemplate(): TemplateData {
  const headers = [
    'name',
    'platform',
    'type',
    'channel',
    'post_type',
    'post_link',
    'content_views',
    'expense',
    'pic',
    'posting_date',
    'language',
    'tier',
    'integration_enable',
    'kol_db_id'
  ];

  const exampleRows = [
    [
      'John Smith Twitter Post',
      'Twitter',
      'Airdrop',
      '@johnsmith_official',
      'Tweet',
      'https://twitter.com/johnsmith_official/status/example123',
      '15000',
      '500',
      'Alice Johnson;Bob Smith',
      '2024-01-15',
      'EN',
      'Tier 1',
      'true',
      ''
    ],
    [
      'Sarah Chen YouTube Video',
      'Youtube',
      'General',
      '@sarahchentech',
      'Video',
      'https://youtube.com/watch?v=example456',
      '85000',
      '1200',
      'Carol Davis',
      '2024-02-20',
      'VN',
      'Tier 2',
      'false',
      ''
    ],
    [
      'Mike Gaming Telegram',
      'Telegram',
      'Defi',
      '@mikerod_gaming',
      'Post',
      'https://t.me/mikerod_gaming/example789',
      '45000',
      '800',
      'David Wilson;Eva Martinez',
      '2024-03-10',
      'RU',
      'Tier 1',
      'true',
      ''
    ]
  ];

  return { headers, exampleRows };
}

export function generateContentIdTemplate(): TemplateData {
  const headers = [
    'content_id',
    'ticket_time',
    'tag1',
    'tag2',
    'tag3',
    'tag4',
    'tag5',
    'pic',
    'description'
  ];

  const exampleRows = [
    [
      'CONTENT-2024-001',
      '3/26/2025',
      'Marketing',
      'Social Media',
      'Campaign',
      'Q1',
      '',
      'Alice Johnson;Bob Smith',
      'Q1 marketing campaign content for social media platforms'
    ],
    [
      'CONTENT-2024-002',
      '4/15/2025',
      'Development',
      'Tech',
      'Backend',
      'API',
      'Documentation',
      'Carol Davis;David Wilson',
      'Backend API development content for technical documentation'
    ],
    [
      'CONTENT-2024-003',
      '5/10/2025',
      'Design',
      'UI/UX',
      'Frontend',
      'Components',
      '',
      'Eva Martinez;Frank Chen',
      'Frontend design system content and component library updates'
    ]
  ];

  return { headers, exampleRows };
}

export function downloadCSVTemplate(tableType: 'kol_db' | 'kol_tracker' | 'content_id') {
  let templateData: TemplateData;
  let filename: string;

  if (tableType === 'kol_db') {
    templateData = generateKolDbTemplate();
    filename = 'kol_dictionary_template.csv';
  } else if (tableType === 'kol_tracker') {
    templateData = generateKolTrackerTemplate();
    filename = 'kol_posting_links_template.csv';
  } else {
    templateData = generateContentIdTemplate();
    filename = 'content_id_template.csv';
  }

  // Create CSV content
  const csvContent = [
    // Headers row
    templateData.headers.map(header => `"${header}"`).join(','),
    // Example data rows
    ...templateData.exampleRows.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    )
  ].join('\n');

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export function getTemplateInstructions(tableType: 'kol_db' | 'kol_tracker' | 'content_id'): string[] {
  if (tableType === 'kol_db') {
    return [
      "• creator_name is required",
      "• social_media_type: Twitter, Telegram, Youtube, Other",
      "• region: EN, VN, RU",
      "• content_type: Twitter - Tweet, Twitter - Thread/long post, Telegram - post",
      "• payment_structure: One time payment, Milestone based, Quarterly",
      "• For content_type field, separate multiple values with semicolons (;)",
      "• Use date format: YYYY-MM-DD (e.g., 2024-01-15)",
      "• Numbers: use plain numbers without currency symbols"
    ];
  } else if (tableType === 'kol_tracker') {
    return [
      "• name and platform are required fields",
      "• platform: Twitter, Telegram, Youtube, Other",
      "• type: Airdrop, General, Defi",
      "• language: EN, VN, RU",
      "• For pic field, separate multiple values with semicolons (;)",
      "• Use date format: YYYY-MM-DD (e.g., 2024-01-15)",
      "• Boolean fields: use 'true' or 'false'",
      "• Numbers: use plain numbers without currency symbols",
      "• kol_db_id can be left empty if aligning KOLs manually",
      "• Topics and Business Units will be assigned manually in the system"
    ];
  } else {
    return [
      "• content_id is required and should be unique",
      "• Use date format: MM/DD/YYYY (e.g., 3/26/2025) for ticket_time",
      "• Use tag1, tag2, tag3, tag4, tag5 columns for different tags",
      "• All tag columns will be combined into a single tags array",
      "• Leave tag columns empty if you don't need all 5 tags",
      "• For pic field, separate multiple values with semicolons (;)",
      "• description is optional but recommended for better organization",
      "• Make sure PIC names match existing users in the system"
    ];
  }
}
